import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconHome, IconBell, IconUsers, IconSettings, IconLogout, IconX, IconFolder } from './Icons';

/**
 * Phase 3.F — Customer-app sidebar.
 *
 * Mounted by ``CustomerHome`` as the persistent left rail (right rail
 * in RTL flex order). Three nav items + a footer with the user's
 * identity and a logout link. Designed to feel quieter than the
 * admin sidebar — fewer items, slightly warmer chrome — so it
 * doesn't drown a one-site-one-manager customer in chrome.
 *
 * Mobile (< 768px): the sidebar is hidden by CSS; ``CustomerHome``
 * shows a top header with a hamburger button, and toggling that
 * passes ``mobileOpen`` so this component re-renders as a drawer.
 *
 * Items:
 *   - Home (always)
 *   - Alerts (always; badge for unread)
 *   - Users (managers only — viewers hide the entry)
 *   - Logout (footer)
 */
function CustomerSidebar({
  user,
  alertCount = 0,
  onLogout,
  mobileOpen = false,
  onMobileClose = () => {},
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Show the users page only to users with explicit user-management
  // permission. Before the PR 2 redesign this was a binary role
  // check; now any template that includes ``can_manage_users`` ==
  // True (today: primary_manager) shows the entry.
  const canManageUsers = !!user?.permissions?.can_manage_users;

  // When viewing a specific site, surface a "קבוצות" item that jumps
  // to that site's groups page. This used to live as an inline chip
  // on the site page itself, but managers preferred it tucked into
  // the side nav so the gates list/map controls stay edge-to-edge.
  // Pattern: /sites/<id>, /sites/<id>/anything.
  const siteMatch = location.pathname.match(/^\/sites\/(\d+)(?:\/|$)/);
  const currentSiteId = siteMatch ? siteMatch[1] : null;

  const items = [
    {
      key: 'home',
      label: 'בית',
      icon: <IconHome className="icon" />,
      isActive: (path) =>
        path === '/' || (path.startsWith('/sites') && !path.includes('/groups')),
      go: () => navigate('/'),
    },
  ];

  if (currentSiteId) {
    items.push({
      key: 'groups',
      label: 'קבוצות שערים',
      icon: <IconFolder className="icon" />,
      isActive: (path) => path.startsWith(`/sites/${currentSiteId}/groups`),
      go: () => navigate(`/sites/${currentSiteId}/groups`),
    });
  }

  items.push({
    key: 'alerts',
    label: 'התראות',
    icon: <IconBell className="icon" />,
    badge: alertCount > 0 ? alertCount : null,
    isActive: (path) => path === '/alerts',
    go: () => navigate('/alerts'),
  });

  if (canManageUsers) {
    items.push({
      key: 'users',
      label: 'ניהול משתמשים',
      icon: <IconUsers className="icon" />,
      isActive: (path) => path.startsWith('/users'),
      go: () => navigate('/users'),
    });
  }

  // Settings is a Manager-only home for sensitive account controls —
  // today: granting / revoking Huri support access and reopening a
  // handed-off project. Viewers never see it.
  const isManager = user?.role === 'manager';
  if (isManager) {
    items.push({
      key: 'settings',
      label: 'הגדרות',
      icon: <IconSettings className="icon" />,
      isActive: (path) => path.startsWith('/settings'),
      go: () => navigate('/settings'),
    });
  }

  const handleNav = (item) => {
    item.go();
    if (mobileOpen) onMobileClose();
  };

  // The same content renders in both desktop fixed-rail and mobile
  // drawer modes; CSS classes on the wrapper handle the difference.
  const className = mobileOpen
    ? 'customer-sidebar customer-sidebar-mobile-open'
    : 'customer-sidebar';

  return (
    <>
      {mobileOpen && (
        <div
          className="customer-sidebar-backdrop"
          onClick={onMobileClose}
          aria-label="סגור תפריט"
        />
      )}
      <nav className={className} aria-label="ניווט ראשי">
        <div className="customer-sidebar-brand">
          <img
            src="/logo-icon.svg"
            alt="Huri-Connect"
            className="customer-sidebar-brand-logo"
          />
          <div className="customer-sidebar-brand-text">
            <div className="customer-sidebar-brand-name">Huri-Connect</div>
            <div className="customer-sidebar-brand-tagline">ניהול שערים</div>
          </div>
          {mobileOpen && (
            <button
              type="button"
              className="customer-sidebar-close"
              onClick={onMobileClose}
              aria-label="סגור תפריט"
            >
              <IconX size={18} />
            </button>
          )}
        </div>

        <ul className="customer-sidebar-menu">
          {items.map((item) => {
            const active = item.isActive(location.pathname);
            return (
              <li
                key={item.key}
                className={active ? 'active' : ''}
                onClick={() => handleNav(item)}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.badge != null && (
                  <span className="customer-sidebar-badge">{item.badge}</span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="customer-sidebar-footer">
          {user?.name && (
            <div className="customer-sidebar-user">{user.name}</div>
          )}
          <button
            type="button"
            className="customer-sidebar-logout"
            onClick={() => {
              if (mobileOpen) onMobileClose();
              onLogout();
            }}
          >
            <IconLogout className="icon" />
            <span>יציאה</span>
          </button>
        </div>
      </nav>
    </>
  );
}

export default CustomerSidebar;
